import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Person } from "@/Common/Models/Person/Person";
import { GenderLookup } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { MatchingCriteriaService } from "@/Common/Services/PatientSearchMatching/MatchingCriteriaService";

function PersonSimpleInfoDisplay(props: Readonly<{ person: Person, currentPersonContext: Person }>): JSX.Element {
    const { getText } = useTranslation();
    const person = props.person;
    const personInfo = useMemo(
        () => ({
            address: person.GetFormattedAddress(),
            dateOfBirth: person.GetFormattedDateOfBirth(),
            phone1: person.GetFormattedPhonesByPriorityWithPosition(0),
            phone2: person.GetFormattedPhonesByPriorityWithPosition(1),
            phone3: person.GetFormattedPhonesByPriorityWithPosition(2),
            genderLookup: cacheStore.GetLookupById(person.GenderLookupId)
        }),
        [person]
    );

    const currentPerson = props.currentPersonContext;
    const matches = useMemo(() => MatchingCriteriaService.GetMatchingCriteriaForPerson(currentPerson, person), [currentPerson, person]);

    const hlClass = "highlight-yellow";

    return (
        <div>
            <h4 className="margin-none-top margin-none-bottom"><span className={matches?.firstName || matches?.lastName ? hlClass : ""}>{person.Name}</span>
                {personInfo.genderLookup && personInfo.genderLookup.LookupCode !== GenderLookup.Other && (
                    <span className={`myle-icon-16 ${personInfo.genderLookup.LookupCode.toLowerCase()}`} />
                )}
            </h4>
            <ul>
                <li>
                    <strong>{sessionStore.getDefaultHealthCardLabel()} </strong>
                    <span>
                        <span className={matches?.healthCardNumber ? hlClass : ""}>{person.GetFormattedNAM()}</span>
                        {person.GetFormattedNAMExpirationDateString()}
                    </span>
                </li>
                <li>
                    <strong>{getText("FileNumbersLabel")} </strong>
                    <span className={matches?.identifier01 ? hlClass : ""}>{person.Patient?.PatientResponse.Identifier01}</span>
                </li>
                <li>
                    <strong>{getText("DateofBirthLabel")} </strong>
                    <span className={matches?.dateOfBirth ? hlClass : ""}>{`${personInfo.dateOfBirth} ${person.GetFormattedAge()}`}</span>
                </li>
                {personInfo.phone1 && (
                    <li>
                        <span className="myle-icon-16 phone" />
                        <span className={`${matches?.phone1 ? hlClass : ""}`}>{personInfo.phone1}</span>
                    </li>
                )}
                {personInfo.phone2 && (
                    <li>
                        <span className="myle-icon-16 phone" />
                        <span className={`${matches?.phone2 ? hlClass : ""}`}>{personInfo.phone2}</span>
                    </li>
                )}
                {personInfo.phone3 && (
                    <li>
                        <span className="myle-icon-16 phone" />
                        <span className={`${matches?.phone3 ? hlClass : ""}`}>{personInfo.phone3}</span>
                    </li>
                )}
                {personInfo.address && (
                    <li>
                        <span className="myle-icon-16 pin" />
                        <span className={`${matches?.address ? hlClass : ""}`}>{personInfo.address}</span>
                    </li>
                )}
            </ul>
        </div>
    );
}

export { PersonSimpleInfoDisplay };
